/**
 * TEMP-H-002: Stage Actions Component
 * 
 * Per-stage control actions: skip, approve, request revision.
 * Context-aware rendering with permission gating and confirmation flows.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  SkipForward,
  CheckCircle,
  MessageCircle,
  Loader2,
  Shield,
  Music,
  Headphones
} from 'lucide-react';
import { useControlQueries } from '@/hooks/useControlQueries';
import { useControlSignals } from '@/hooks/useControlSignals';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { useExecutionEvents } from '@/hooks/useExecutionEvents';
import { cn } from '@/utils/cn';
import type { StageType } from '@/types/execution';

interface StageActionsProps {
  executionId: string;
  stage: StageType;
  currentStage: StageType | null;
  className?: string;
  onAction?: (action: string, stage: StageType) => void;
}

interface StageActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant: 'primary' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md';
}

const StageActionButton: React.FC<StageActionButtonProps> = ({
  icon,
  label,
  onClick,
  disabled = false,
  loading = false,
  variant,
  size = 'md'
}) => {
  const variants = {
    primary: 'bg-blue-600/20 text-blue-300 border-blue-600/50 hover:bg-blue-600/30 hover:border-blue-500/50',
    success: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/50 hover:bg-emerald-600/30 hover:border-emerald-500/50',
    warning: 'bg-amber-600/20 text-amber-300 border-amber-600/50 hover:bg-amber-600/30 hover:border-amber-500/50',
    danger: 'bg-red-600/20 text-red-300 border-red-600/50 hover:bg-red-600/30 hover:border-red-500/50'
  };

  const sizes = {
    sm: 'px-2 py-1 text-xs gap-1.5',
    md: 'px-3 py-2 text-sm gap-2'
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || loading}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      className={cn(
        'flex items-center rounded-md font-medium transition-all border backdrop-blur-sm',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size]
      )}
    >
      {loading ? (
        <Loader2 className={cn('animate-spin', size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')} />
      ) : (
        icon
      )}
      {label}
    </motion.button>
  );
};

const StageActions: React.FC<StageActionsProps> = ({
  executionId,
  stage,
  currentStage,
  className,
  onAction
}) => {
  const { isAdmin } = useServerAuth();
  const { data: queries } = useControlQueries(executionId);
  const signals = useControlSignals(executionId, {
    onSuccess: (response) => {
      onAction?.(response.signalType, stage);
    }
  });

  // For music stage, check if base music has artifacts to gate master/sidechain controls
  const { eventsByStage } = useExecutionEvents({
    executionId,
    enabled: stage === 'music',
    stage: ['music']
  });

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showMusicMasterDialog, setShowMusicMasterDialog] = useState(false);
  const [showMusicSidechainDialog, setShowMusicSidechainDialog] = useState(false);

  // Permission and state checks
  const canControl = isAdmin && queries?.canControl;
  const isCurrentStage = currentStage === stage;
  const isAfterCurrentStage = currentStage && getStageOrder(stage) > getStageOrder(currentStage);
  const isBeforeCurrentStage = currentStage && getStageOrder(stage) < getStageOrder(currentStage);

  // Music-specific gating logic: check if base music stage has artifacts
  const musicEvents = eventsByStage?.get('music') || [];
  const musicHasArtifacts = musicEvents.some(event => 
    event.event_type === 'content_ready' && event.artifactRefs.length > 0
  );
  const canLaunchMusicExtensions = stage === 'music' && (musicHasArtifacts || queries?.currentStage === 'music');

  // Stage ordering for skip logic
  function getStageOrder(stageType: StageType): number {
    const order: StageType[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos'];
    return order.indexOf(stageType);
  }

  // Don't show actions if no permission
  if (!canControl) {
    return (
      <div className={cn('flex items-center text-xs text-slate-500', className)}>
        <Shield className="w-3 h-3 mr-1" />
        Admin only
      </div>
    );
  }

  // Don't show actions for future stages (except skip)
  if (isAfterCurrentStage) {
    return (
      <div className={cn('flex items-center text-xs text-slate-500', className)}>
        Pending
      </div>
    );
  }

  // Handle action functions
  const handleSkip = async () => {
    setShowSkipDialog(true);
  };

  const handleApprove = async () => {
    setShowApproveDialog(true);
  };

  const handleRequestRevision = async () => {
    setShowRevisionDialog(true);
  };

  const handleStartMusicMaster = async () => {
    setShowMusicMasterDialog(true);
  };

  const handleStartMusicSidechain = async () => {
    setShowMusicSidechainDialog(true);
  };

  const confirmSkip = async () => {
    try {
      await signals.skipToStage({
        stage,
        reason: `Manual skip to ${stage} stage via stage actions`
      });
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setShowSkipDialog(false);
    }
  };

  const confirmApprove = async (notes?: string) => {
    try {
      await signals.approveStage({
        stage,
        notes
      });
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setShowApproveDialog(false);
    }
  };

  const confirmRevision = async (reason: string, specificFeedback?: string) => {
    try {
      await signals.requestRevision({
        stage,
        reason,
        specificFeedback
      });
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setShowRevisionDialog(false);
    }
  };

  const confirmMusicMaster = async (reason?: string) => {
    try {
      await signals.startMusicMaster({
        reason: reason || `Start music master stage via stage actions`
      });
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setShowMusicMasterDialog(false);
    }
  };

  const confirmMusicSidechain = async (reason?: string) => {
    try {
      await signals.startMusicSidechain({
        reason: reason || `Start music sidechain stage via stage actions`
      });
    } catch (error) {
      // Error handled by signals hook
    } finally {
      setShowMusicSidechainDialog(false);
    }
  };

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        {/* Skip To Stage - only for future stages */}
        {isAfterCurrentStage && (
          <StageActionButton
            icon={<SkipForward className="w-4 h-4" />}
            label="Skip To"
            onClick={handleSkip}
            disabled={signals.isLoading}
            loading={signals.isLoading}
            variant="warning"
            size="sm"
          />
        )}

        {/* Current/Past Stage Actions */}
        {(isCurrentStage || isBeforeCurrentStage) && (
          <>
            {/* Approve */}
            <StageActionButton
              icon={<CheckCircle className="w-4 h-4" />}
              label="Approve"
              onClick={handleApprove}
              disabled={signals.isLoading}
              loading={signals.isLoading}
              variant="success"
              size="sm"
            />

            {/* Request Revision */}
            <StageActionButton
              icon={<MessageCircle className="w-4 h-4" />}
              label="Revision"
              onClick={handleRequestRevision}
              disabled={signals.isLoading}
              loading={signals.isLoading}
              variant="warning"
              size="sm"
            />
          </>
        )}

        {/* Music-specific actions - only show on music stage when base music has artifacts */}
        {canControl && canLaunchMusicExtensions && (
          <>
            {/* Start Music Master */}
            <StageActionButton
              icon={<Music className="w-4 h-4" />}
              label="Master"
              onClick={handleStartMusicMaster}
              disabled={signals.isLoading}
              loading={signals.isLoading}
              variant="primary"
              size="sm"
            />

            {/* Start Music Sidechain */}
            <StageActionButton
              icon={<Headphones className="w-4 h-4" />}
              label="Sidechain"
              onClick={handleStartMusicSidechain}
              disabled={signals.isLoading}
              loading={signals.isLoading}
              variant="primary"
              size="sm"
            />
          </>
        )}
      </div>

      {/* Dialogs will be implemented in separate components */}
      {showSkipDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              Skip to {stage.charAt(0).toUpperCase() + stage.slice(1)} Stage
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              This will skip the current execution to the {stage} stage. Any incomplete work in previous stages will be bypassed.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSkipDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSkip}
                disabled={signals.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-all disabled:opacity-50"
              >
                {signals.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Skip to Stage
              </button>
            </div>
          </div>
        </div>
      )}

      {showApproveDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              Approve {stage.charAt(0).toUpperCase() + stage.slice(1)} Stage
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Mark this stage as approved and proceed to the next stage.
            </p>
            <textarea
              placeholder="Optional approval notes..."
              className="w-full p-3 bg-slate-700/50 border border-slate-600/50 rounded-md text-slate-200 text-sm mb-4 resize-none"
              rows={3}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowApproveDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmApprove()}
                disabled={signals.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition-all disabled:opacity-50"
              >
                {signals.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Approve Stage
              </button>
            </div>
          </div>
        </div>
      )}

      {showRevisionDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              Request Revision for {stage.charAt(0).toUpperCase() + stage.slice(1)}
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Provide specific feedback for revision. Be clear about what needs to be improved.
            </p>
            <textarea
              placeholder="Reason for revision (required)..."
              className="w-full p-3 bg-slate-700/50 border border-slate-600/50 rounded-md text-slate-200 text-sm mb-3 resize-none"
              rows={3}
              required
            />
            <textarea
              placeholder="Specific feedback (optional)..."
              className="w-full p-3 bg-slate-700/50 border border-slate-600/50 rounded-md text-slate-200 text-sm mb-4 resize-none"
              rows={2}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRevisionDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmRevision('Revision requested via stage actions')}
                disabled={signals.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-all disabled:opacity-50"
              >
                {signals.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Request Revision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Music Master Dialog */}
      {showMusicMasterDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              Start Music Master Stage
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              This will launch the music mastering stage to create professional-quality audio from the base music track.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowMusicMasterDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmMusicMaster()}
                disabled={signals.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50"
              >
                {signals.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Start Music Master
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Music Sidechain Dialog */}
      {showMusicSidechainDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              Start Music Sidechain Stage
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              This will launch the music sidechaining stage to create dynamic audio processing effects from the base music track.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowMusicSidechainDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmMusicSidechain()}
                disabled={signals.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50"
              >
                {signals.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Start Music Sidechain
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StageActions;